import { forwardRef, useCallback, useEffect, useRef, useState, type ComponentPropsWithoutRef } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { t, type Lang } from '../lib/i18n'

// R456: every staff password field gets a show/hide eye. The toggle changes ONLY the input's
// type; every other prop (id, name, autoComplete, value, onChange, data-*) passes through
// untouched. The value is never read, logged or stored here. State is component-local, so it
// resets on unmount/navigation, and it goes back to hidden when the field's own form submits
// (a native listener on input.form; no submit handler is touched).
type PasswordInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'type'> & { lang: Lang }

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { lang, className, disabled, ...inputProps },
  ref,
) {
  const [shown, setShown] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  useEffect(() => {
    const form = inputRef.current?.form
    if (!form) return
    const hideOnSubmit = () => setShown(false)
    form.addEventListener('submit', hideOnSubmit)
    return () => form.removeEventListener('submit', hideOnSubmit)
  }, [])

  const label = t(lang, shown ? 'hidePassword' : 'showPassword')

  return (
    <div className="relative">
      <input
        {...inputProps}
        ref={setRefs}
        type={shown ? 'text' : 'password'}
        disabled={disabled}
        className={`${className ?? ''} pe-12`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-pressed={shown}
        aria-label={label}
        title={label}
        disabled={disabled}
        data-password-toggle=""
        className="absolute end-0 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {shown ? <EyeOff aria-hidden="true" className="h-5 w-5" /> : <Eye aria-hidden="true" className="h-5 w-5" />}
      </button>
    </div>
  )
})
